import { parseUTCStringAsLocal, toFakeUtcISO } from "@framework/provider-asu";

import type { IChartData } from "../../scheme/components/analytics/interfaces";
import type { IMultiAnalysisDataMark, IMultiAnalysisTimeRange, MultiAnalysisTimeValue } from "../types";

const MIN_FRAME_DURATION_MS = 60_000;
const FULL_RANGE_TOLERANCE_MS = 1000;

const normalizeChartDateString = (value: string): string => value.replace("Z", "");

const timeValueToChartDateString = (value: Date | string): string =>
  value instanceof Date ? normalizeChartDateString(value.toISOString()) : normalizeChartDateString(value);

const toTimestamp = (value: MultiAnalysisTimeValue): number | null => {
  if (!value) {
    return null;
  }

  const date = new Date(timeValueToChartDateString(value instanceof Date ? value : value));

  return Number.isFinite(date.getTime()) ? date.getTime() : null;
};

const cloneTimeValue = (value: MultiAnalysisTimeValue): Date | string | null | undefined => {
  if (value instanceof Date) {
    return new Date(value.getTime());
  }

  return value;
};

const clampRatio = (value: number): number => Math.min(Math.max(value, 0), 1);

const timestampToChartDateString = (timestamp: number, reference: string): string => {
  const fakeUtcTimestamp = timestamp - new Date(timestamp).getTimezoneOffset() * 60_000;
  const value = new Date(fakeUtcTimestamp).toISOString();

  return reference.includes("Z") ? value : normalizeChartDateString(value);
};

const toRangeValue = (timestamp: number, reference: MultiAnalysisTimeValue): Date | string =>
  reference instanceof Date ? new Date(timestamp) : timestampToChartDateString(timestamp, String(reference ?? ""));

export const normalizeTimeRange = (range: IMultiAnalysisTimeRange): { start: number; end: number } | null => {
  const start = toTimestamp(range.start);
  const end = toTimestamp(range.end);

  if (start === null || end === null) {
    return null;
  }

  return start <= end ? { start, end } : { start: end, end: start };
};

export const cloneTimeRange = (range: IMultiAnalysisTimeRange): IMultiAnalysisTimeRange => ({
  start: cloneTimeValue(range.start),
  end: cloneTimeValue(range.end),
});

export const isFullTimeRange = (frame: IMultiAnalysisTimeRange, global: IMultiAnalysisTimeRange): boolean => {
  const frameBounds = normalizeTimeRange(frame);
  const globalBounds = normalizeTimeRange(global);

  if (!frameBounds || !globalBounds) {
    return false;
  }

  return (
    Math.abs(frameBounds.start - globalBounds.start) <= FULL_RANGE_TOLERANCE_MS &&
    Math.abs(frameBounds.end - globalBounds.end) <= FULL_RANGE_TOLERANCE_MS
  );
};

export const timeToRatio = (timestamp: number, global: IMultiAnalysisTimeRange): number => {
  const bounds = normalizeTimeRange(global);

  if (!bounds) {
    return 0;
  }

  const duration = bounds.end - bounds.start;

  if (duration <= 0) {
    return 0;
  }

  return (timestamp - bounds.start) / duration;
};

export const getFramePlotRatios = (
  frame: IMultiAnalysisTimeRange,
  global: IMultiAnalysisTimeRange,
): { leftRatio: number; rightRatio: number } => {
  if (isFullTimeRange(frame, global)) {
    return { leftRatio: 0, rightRatio: 1 };
  }

  const frameBounds = normalizeTimeRange(frame);
  const globalBounds = normalizeTimeRange(global);

  if (!frameBounds || !globalBounds) {
    return { leftRatio: 0, rightRatio: 1 };
  }

  const leftRatio = clampRatio(timeToRatio(frameBounds.start, global));
  const rightRatio = clampRatio(timeToRatio(frameBounds.end, global));

  return {
    leftRatio: Math.min(leftRatio, rightRatio),
    rightRatio: Math.max(leftRatio, rightRatio),
  };
};

export const createRangeFromTimestamps = (
  start: number,
  end: number,
  reference: MultiAnalysisTimeValue,
): IMultiAnalysisTimeRange => ({
  start: toRangeValue(start, reference),
  end: toRangeValue(end, reference),
});

export const clampFrameRange = (
  frame: IMultiAnalysisTimeRange,
  global: IMultiAnalysisTimeRange,
): IMultiAnalysisTimeRange => {
  const globalBounds = normalizeTimeRange(global);
  const frameBounds = normalizeTimeRange(frame);

  if (!globalBounds || !frameBounds) {
    return cloneTimeRange(global);
  }

  const globalDuration = globalBounds.end - globalBounds.start;
  const minDuration = Math.min(MIN_FRAME_DURATION_MS, globalDuration);
  let start = frameBounds.start;
  let end = frameBounds.end;

  if (end - start < minDuration) {
    end = start + minDuration;
  }

  if (start < globalBounds.start) {
    start = globalBounds.start;
    end = Math.max(start + minDuration, end);
  }

  if (end > globalBounds.end) {
    end = globalBounds.end;
    start = Math.min(end - minDuration, start);
  }

  if (start < globalBounds.start) {
    start = globalBounds.start;
  }

  return createRangeFromTimestamps(start, end, global.start);
};

export const moveFrameRange = (
  frame: IMultiAnalysisTimeRange,
  global: IMultiAnalysisTimeRange,
  deltaMs: number,
): IMultiAnalysisTimeRange => {
  const globalBounds = normalizeTimeRange(global);
  const frameBounds = normalizeTimeRange(frame);

  if (!globalBounds || !frameBounds) {
    return frame;
  }

  const duration = frameBounds.end - frameBounds.start;
  let start = frameBounds.start + deltaMs;
  let end = start + duration;

  if (start < globalBounds.start) {
    start = globalBounds.start;
    end = start + duration;
  }

  if (end > globalBounds.end) {
    end = globalBounds.end;
    start = end - duration;
  }

  return createRangeFromTimestamps(start, end, global.start);
};

export const ratioToTimestamp = (ratio: number, global: IMultiAnalysisTimeRange): number => {
  const bounds = normalizeTimeRange(global);

  if (!bounds) {
    return 0;
  }

  const clampedRatio = clampRatio(ratio);

  return bounds.start + (bounds.end - bounds.start) * clampedRatio;
};

export const parseMultiAnalysisChartTime = (value: string | Date): number =>
  new Date(value instanceof Date ? timeValueToChartDateString(value) : normalizeChartDateString(value)).getTime();

const shiftMultiAnalysisGlobalTimeValue = (
  value: MultiAnalysisTimeValue,
  dbTz: number,
  direction: 1 | -1,
): MultiAnalysisTimeValue => {
  if (!value) {
    return value;
  }

  const parsed = value instanceof Date ? value : parseUTCStringAsLocal(value);
  const shifted = new Date(parsed.getTime() + direction * dbTz * 60_000);

  if (value instanceof Date) {
    return shifted;
  }

  return toFakeUtcISO(shifted, dbTz);
};

export const denormalizeMultiAnalysisGlobalTimeValue = (
  value: MultiAnalysisTimeValue,
  dbTz: number,
): MultiAnalysisTimeValue => shiftMultiAnalysisGlobalTimeValue(value, dbTz, -1);

export const denormalizeMultiAnalysisGlobalRange = (
  range: IMultiAnalysisTimeRange,
  dbTz: number,
): IMultiAnalysisTimeRange => ({
  start: denormalizeMultiAnalysisGlobalTimeValue(range.start, dbTz),
  end: denormalizeMultiAnalysisGlobalTimeValue(range.end, dbTz),
});

export const normalizeMultiAnalysisMeasureDate = (date: string, dbTz: number): string => toFakeUtcISO(date, dbTz);

export const normalizeMultiAnalysisTrendSeriesDates = (series: readonly IChartData[], dbTz: number): IChartData[] =>
  series.map(item => ({
    ...item,
    items: item.items.map(point => ({
      ...point,
      date: normalizeMultiAnalysisMeasureDate(String(point.date), dbTz),
    })),
  }));

export const normalizeMultiAnalysisDataMarkDates = (
  marks: readonly IMultiAnalysisDataMark[],
  dbTz: number,
): IMultiAnalysisDataMark[] =>
  marks.map(mark => ({
    ...mark,
    measureDate: mark.measureDate || mark.date,
    date: normalizeMultiAnalysisMeasureDate(mark.date, dbTz),
  }));
